import {
  CheckSquare,
  LayoutDashboard, Users, Kanban,
  Settings, BarChart3, CalendarDays,
  UserCheck, CreditCard, CalendarRange,
  Calculator, PhoneCall,
  ShoppingBag, Gift, Award, GraduationCap, CalendarClock,
  MessageSquare, Bot, Target, Filter, Handshake, ListChecks, Link2,
  Gauge, Ticket, Mic, Sunrise, FileSignature, TrendingUp, ClipboardCheck, Landmark, Zap, Megaphone, LifeBuoy, Dumbbell, Receipt, Layers, Inbox, Salad, MoreHorizontal, BookOpen, Activity, PartyPopper, MessagesSquare
} from 'lucide-react';

/* Navigation in Kategorien. Sichtbarkeitslogik:
   - Der Inhaber (Rolle admin bzw. Admin-Berechtigungen) sieht alles.
   - Mitarbeiter sehen nur Einträge, deren requiredPermission ihre Rolle
     hat (RBAC unter Einstellungen → Team); Einträge ohne requiredPermission
     sieht jeder.
   - nurInhaber: true (am Eintrag oder an der ganzen Sektion) blendet den
     Punkt für Mitarbeiter IMMER aus — unabhängig von Berechtigungen.
   - schalter: 'name' blendet den Punkt für ALLE aus, solange die Funktion
     aus ist (GET /api/funktionen, backend/funktionen.py). */

export const NAV_SECTIONS = [
  /* Struktur 12.10.2026 (Betreiber: „Seitenleiste soll optimiert werden"): acht aufklappbare Bereiche nach dem, was man
     tut, statt neun langer Listen. „Start" steht immer offen oben, darunter die selbst angehefteten Favoriten
     (Sidebar.js). Pfade, Berechtigungen und nurInhaber je Eintrag sind gegenüber dem 22.09.2026 UNVERÄNDERT — nur die
     Gruppierung ist neu. `versteckt` nimmt abgelöste Seiten aus dem Menü; die Route und ihr Schutz bleiben. */
  {
    titel: 'Start', icon: Sunrise, fest: true,
    items: [
      { path: '/heute', icon: Sunrise, label: 'Heute', requiredPermission: 'dashboard.view_own' },
      { path: '/posteingang', icon: MessageSquare, label: 'Posteingang', requiredPermission: 'superchat.view' },
    ],
  },
  {
    titel: 'Studio', icon: CalendarRange,
    items: [
      { path: '/kalender', icon: CalendarDays, label: 'Kalender', requiredPermission: null },
      /* 29.10.2026: Leitung ↔ Trainer-Link, Wochentermine mit Lesebestätigung */
      { path: '/trainer-chat', icon: MessagesSquare, label: 'Trainer-Chat', requiredPermission: 'leads.edit' },
      { path: '/kursplan', icon: CalendarRange, label: 'Kursplan', requiredPermission: null },
      { path: '/empfang', icon: UserCheck, label: 'Empfang & Check-in', requiredPermission: 'checkin.view' },
      { path: '/trainer', icon: ClipboardCheck, label: 'Anwesenheit (Trainer)', requiredPermission: 'app.trainer_access' },
      /* Einsatzplanung Schritt 1 (16.10.2026): Trainer-Profile, Abgleich der Kennungen, Qualifikation */
      { path: '/einsatzplan/trainer', icon: Users, label: 'Trainer', requiredPermission: 'leads.view_own' },
      { path: '/gastpaesse', icon: Dumbbell, label: 'Gastpässe', requiredPermission: 'verkauf.view_vertraege' },
      { path: '/coaching', icon: Dumbbell, label: 'P1 Coaching', requiredPermission: 'leads.view_own' },
      /* Urban Sports Club (30.09.2026): Gäste, Stammgäste, Abgleich mit der Auszahlung */
      { path: '/usc', icon: Dumbbell, label: 'Urban Sports Club', requiredPermission: 'checkin.view' },
    ],
  },
  {
    titel: 'Mitglieder', icon: Users,
    items: [
      { path: '/mitgliedschaften', icon: CreditCard, label: 'Mitglieder', requiredPermission: null },
      { path: '/vertraege', icon: FileSignature, label: 'Verträge', requiredPermission: 'verkauf.view_vertraege' },
      { path: '/kursbestand', icon: Users, label: 'Kursbestand', requiredPermission: 'kursplan.view' },
      { path: '/graduierung', icon: Award, label: 'Graduierung', requiredPermission: 'app.trainer_access' },
      { path: '/praemien', icon: Gift, label: 'Prämien', requiredPermission: 'checkin.create' },
      /* Support & Umstieg (29.09.2026): Anfragen aus der App + Fortschritt Octiv -> P1 App */
      { path: '/support', icon: LifeBuoy, label: 'Support & Umstieg', requiredPermission: 'leads.view_own' },
    ],
  },
  {
    titel: 'Training', icon: Dumbbell,
    items: [
      { path: '/planer', icon: Layers, label: 'Trainingsplaner', requiredPermission: 'trainingsplaner.view' },
      /* Lehrplan (15.10.2026): Inhalte je Gürtel; lesen kursplan.view, ändern settings.edit_general (Server) */
      { path: '/lehrplan', icon: BookOpen, label: 'Lehrplan', requiredPermission: 'kursplan.view' },
      /* Ernährung (10.10.2026): Angaben, Wochenplan aus P1-Rezepten, Fortschritt mit Ampel */
      { path: '/ernaehrung', icon: Salad, label: 'Ernährung', requiredPermission: 'ernaehrung.view' },
      /* E6 Coach-Cockpit (15.10.2026): Kundenliste · Konversation · Kundenakte für das Ernährungscoaching */
      { path: '/coaching-cockpit', icon: MessageSquare, label: 'Coaching-Cockpit', requiredPermission: 'ernaehrung.view' },
      /* Waagenstation (29.10.2026): Gastmessungen der InBody 270 zuordnen */
      { path: '/waage', icon: Activity, label: 'Waage', requiredPermission: 'ernaehrung.view' },
    ],
  },
  {
    titel: 'Vertrieb', icon: TrendingUp,
    items: [
      { path: '/leads', icon: Users, label: 'Leads', requiredPermission: 'leads.view_own' },
      { path: '/probetrainings', icon: CalendarClock, label: 'Probetrainings', requiredPermission: 'leads.view_own' },
      { path: '/schnell', icon: Mic, label: 'Schnellerfassung', requiredPermission: 'leads.create' },
      {
        path: '/verkauf', icon: Calculator, label: 'Verkaufsrechner', requiredPermission: null,
        children: [
          { path: '/verkauf', label: '1. Bedarf erfassen', step: 1 },
          { path: '/verkauf', label: '2. Empfehlung', step: 2 },
          { path: '/verkauf', label: '3. Tag 1 Angebot', step: 3 },
          { path: '/verkauf', label: '4. Vertrag', step: 4 },
          { path: '/verkauf', label: '5. Follow-up', step: 5 },
          { path: '/verkauf/referenz', label: 'Tarifübersicht (intern)' },
        ],
      },
      { path: '/pipeline', icon: Kanban, label: 'Pipeline', requiredPermission: 'pipeline.view', nurInhaber: true },
      { path: '/vertrieb', icon: PhoneCall, label: 'Vertrieb heute', requiredPermission: 'vertrieb.view', nurInhaber: true },
      { path: '/closer', icon: Handshake, label: 'Abschluss heute', requiredPermission: 'vertrieb.closer', nurInhaber: true },
      /* Lead-Anrufliste (24.09.2026): kalte Leads mit Skript abtelefonieren */
      { path: '/kampagnen/anrufe', icon: PhoneCall, label: 'Lead-Anrufliste', requiredPermission: 'vertrieb.view' },
      { path: '/reaktivierung', icon: PhoneCall, label: 'Reaktivierung', requiredPermission: 'reaktivierung.view', nurInhaber: true },
      /* Vorauszahler aus dem Bestand (25.09.2026): FBC-Ziel fuenf Vorauszahler */
      { path: '/vorauszahler', icon: Handshake, label: 'Vorauszahler (Bestand)', requiredPermission: 'vertrieb.view' },
      { path: '/einschaetzungen', icon: Gauge, label: 'Einschätzung (KI)', requiredPermission: 'leads.view', nurInhaber: true },
      /* v8 M18 (22.10.2026): Einwandbehandlung im Rollenspiel üben — nur sichtbar, wenn KI_VERKAUFSTRAINER an ist */
      { path: '/verkaufstrainer', icon: GraduationCap, label: 'Verkaufstrainer', requiredPermission: 'vertrieb.view', schalter: 'verkaufstrainer' },
      /* Pia (30.10.2026): Assistenz mit Chat und Push — nur sichtbar, wenn KI_ASSISTENZ an ist; Zugang prüft der Server */
      { path: '/pia', icon: Bot, label: 'Pia', requiredPermission: 'vertrieb.view', schalter: 'pia', nurInhaber: true },
      { path: '/copilot', icon: Bot, label: 'Frag dein Business', requiredPermission: 'vertrieb.view' },
    ],
  },
  {
    /* Nobiro Pay: Verträge stehen bei den Mitgliedern, hier das Geld */
    titel: 'Finanzen', icon: Landmark,
    items: [
      /* Lexware v1 (30.09.2026): Monatsrechnungen vorschlagen, freigeben = festschreiben (nur Inhaber) */
      { path: '/rechnungen', icon: Receipt, label: 'Rechnungslauf', requiredPermission: 'mandate.view' },
      /* Finanzen (03.10.2026): Eingangsrechnungen pruefen -> Beleg nach Lexware, Ausgang als Lexware-Spiegel */
      { path: '/finanzen/rechnungen', icon: Inbox, label: 'Rechnungen Ein- & Ausgang', requiredPermission: 'finanzen.view' },
      { path: '/mandate', icon: Landmark, label: 'SEPA-Mandate', requiredPermission: 'mandate.view' },
      { path: '/cashflow', icon: TrendingUp, label: 'Cashflow & Prognose', requiredPermission: 'reports.view', nurInhaber: true },
      { path: '/shop', icon: ShoppingBag, label: 'Shop / WWS', requiredPermission: null },
    ],
  },
  {
    titel: 'Kommunikation', icon: Megaphone,
    items: [
      /* 05.10.2026 (Betreiber): nicht mehr nurInhaber — wer das Recht freigaben.view hat (z. B. die Rolle
         Vertrieb), sieht die Freigabe-Zentrale und gibt dort Entwürfe frei. Ohne das Recht bleibt sie verborgen. */
      { path: '/freigabe', icon: CheckSquare, label: 'Freigabe-Zentrale', requiredPermission: 'freigaben.view' },
      { path: '/rundschreiben', icon: Megaphone, label: 'Rundschreiben', requiredPermission: 'superchat.send' },
      /* v8 M22 (22.10.2026): Redaktionsplan; die Seite läuft ohne KI, nur die KI-Knöpfe hängen an KI_CONTENT */
      { path: '/content', icon: CalendarDays, label: 'Contentplan', requiredPermission: 'superchat.send' },
      /* Coach-Cockpit W1.7: freigegebene Ergebnisse (Zahlen, Fotos ohne Gesicht) — Leitung und Content-Team wie der Contentplan */
      { path: '/testimonials', icon: Award, label: 'Testimonials', requiredPermission: 'superchat.send' },
      { path: '/automatiken', icon: Zap, label: 'Automatiken', requiredPermission: 'settings.view', nurInhaber: true },
      { path: '/funnels', icon: Filter, label: 'Funnels', requiredPermission: 'funnels.view', nurInhaber: true },
      { path: '/kurzlinks', icon: Link2, label: 'Kurzlinks & QR', requiredPermission: 'settings.view', nurInhaber: true },
      { path: '/setter', icon: Bot, label: 'Setter-Entwürfe (KI)', requiredPermission: 'setter.view', nurInhaber: true },
      { path: '/agent', icon: Bot, label: 'KI-Agent', requiredPermission: 'superchat.agent_verwalten', nurInhaber: true },
      { path: '/follow-ups', icon: PhoneCall, label: 'Follow-ups (alt)', requiredPermission: 'follow_ups.view_own', nurInhaber: true, versteckt: true },
    ],
  },
  {
    titel: 'Auswertung', icon: BarChart3,
    items: [
      { path: '/kennzahlen', icon: TrendingUp, label: 'Kennzahlen', requiredPermission: 'dashboard.view_own', nurInhaber: true },
      { path: '/vertriebszahlen', icon: BarChart3, label: 'Vertriebszahlen', requiredPermission: 'vertrieb.kennzahlen' },
      /* Sales Tracking (24.09.2026): Abschlussquote — Berater sehen nur sich */
      { path: '/kennzahlen/vertrieb', icon: Target, label: 'Abschlussquote', requiredPermission: 'vertrieb.view' },
      /* v9 A (23.10.2026): wer trainiert wie oft — Segmente, Verlauf, CSV */
      { path: '/teilnahme', icon: Activity, label: 'Teilnahme', requiredPermission: 'leads.view_all' },
      { path: '/reports', icon: BarChart3, label: 'Berichte', requiredPermission: 'reports.view', nurInhaber: true },
      { path: '/arbeitsbericht', icon: ClipboardCheck, label: 'Arbeitsbericht', requiredPermission: 'team.view', nurInhaber: true },
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard (alt)', requiredPermission: 'dashboard.view_all', nurInhaber: true, versteckt: true },
    ],
  },
  {
    titel: 'Mehr', icon: MoreHorizontal,
    items: [
      { path: '/seminare', icon: GraduationCap, label: 'Seminare', requiredPermission: 'seminare.view' },
      /* 28.10.2026: Anmeldungen zur BMT-Gürtelprüfung (öffentliche Seite /bmt-pruefung) — Rechte wie Seminare */
      { path: '/pruefungen', icon: Award, label: 'Gürtelprüfung', requiredPermission: 'seminare.view' },
      { path: '/vorhaben', icon: ListChecks, label: 'Projekte & Events', requiredPermission: 'vorhaben.view' },
      { path: '/gym-fights-tickets', icon: Ticket, label: 'Gym Fights Tickets', requiredPermission: 'tickets.view' },
      /* 27.10.2026: Eventplanung mit Szenarien, Sponsoren, Kosten Plan/Ist und Ticket-Abgleich */
      { path: '/eventrechner', icon: PartyPopper, label: 'Eventrechner', requiredPermission: 'eventrechner.view' },
      { path: '/mandanten/zinzino', icon: CalendarClock, label: 'Zinzino: Termine & Leads', requiredPermission: 'mandanten.view' },
      { path: '/zinzino-cockpit', icon: Target, label: 'Zinzino-Cockpit', requiredPermission: 'mandanten.view' },
      { path: '/team', icon: Users, label: 'Team', requiredPermission: 'team.view', nurInhaber: true },
      { path: '/settings', icon: Settings, label: 'Einstellungen', requiredPermission: 'settings.view', nurInhaber: true },
    ],
  },
];

/* Vorschlag für die Favoriten, solange jemand noch keine eigenen angeheftet hat */
export const FAVORITEN_STANDARD = ['/verkauf', '/freigabe', '/probetrainings', '/leads', '/empfang'];

/* Flache Liste (Routen-Guard, Alt-Verwendungen) — nurInhaber der Sektion
   wird auf die Einträge vererbt. */
export const NAV_ITEMS = NAV_SECTIONS.flatMap((s) =>
  s.items.map((item) => ({ ...item, nurInhaber: s.nurInhaber || item.nurInhaber || false }))
);

export const SALES_PATHS = ['/verkauf', '/verkaufsrechner', '/bedarfserfassung', '/tag1-angebot', '/vertragsabschluss'];

export const BOTTOM_TABS = {
  admin: [
    { path: '/verkauf', icon: Calculator, label: 'Verkauf' },
    { path: '/heute', icon: Sunrise, label: 'Heute' },
    { path: '/leads', icon: Users, label: 'Leads' },
    { path: '/mitgliedschaften', icon: CreditCard, label: 'Mitglieder' },
  ],
  manager: [
    { path: '/verkauf', icon: Calculator, label: 'Verkauf' },
    { path: '/heute', icon: Sunrise, label: 'Heute' },
    { path: '/leads', icon: Users, label: 'Leads' },
    { path: '/empfang', icon: UserCheck, label: 'Empfang' },
  ],
  agent: [
    { path: '/verkauf', icon: Calculator, label: 'Verkauf' },
    { path: '/heute', icon: Sunrise, label: 'Heute' },
    { path: '/leads', icon: Users, label: 'Leads' },
    { path: '/posteingang', icon: MessageSquare, label: 'Posteingang' },
  ],
};

const FALLBACK_ROLLEN = {
  manager: ['leads.view_own', 'pipeline.view', 'activities.view_own', 'follow_ups.view_own', 'team.view'],
  agent: ['leads.view_own', 'pipeline.view', 'activities.view_own', 'follow_ups.view_own'],
};

function itemSichtbar(item, role, userPermissions, istInhaber) {
  if (istInhaber) return true;
  if (item.nurInhaber) return false;
  if (!item.requiredPermission) return true;
  if (userPermissions) return !!userPermissions[item.requiredPermission];
  const allowed = FALLBACK_ROLLEN[role] || [];
  return allowed.includes(item.requiredPermission);
}

/** Sektionen mit gefilterten Einträgen für die Sidebar; leere Sektionen
 *  fallen weg. */
export function getNavSectionsForRole(role, userPermissions = null, isAdmin = false, funktionen = null) {
  const istInhaber = role === 'admin' || isAdmin;
  return NAV_SECTIONS
    .map((s) => {
      if (s.nurInhaber && !istInhaber) return null;
      const items = s.items.filter((item) => !item.versteckt && (!item.schalter || !!funktionen?.[item.schalter]) &&
        itemSichtbar({ ...item, nurInhaber: s.nurInhaber || item.nurInhaber }, role, userPermissions, istInhaber));
      return items.length ? { titel: s.titel, icon: s.icon, fest: !!s.fest, items } : null;
    })
    .filter(Boolean);
}

/** Flache Liste (Alt-API) — gleiche Sichtbarkeitsregeln wie die Sektionen. */
export function getNavItemsForRole(role, userPermissions = null, isAdmin = false) {
  const istInhaber = role === 'admin' || isAdmin;
  return NAV_ITEMS.filter((item) => itemSichtbar(item, role, userPermissions, istInhaber));
}

export function getBottomTabsForRole(role) {
  if (['admin', 'manager', 'agent'].includes(role)) {
    return BOTTOM_TABS[role] || BOTTOM_TABS.agent;
  }
  return BOTTOM_TABS.agent;
}

export function canAccessRoute(role, path, userPermissions = null, isAdmin = false) {
  if (role === 'admin' || isAdmin) return true;

  const basePath = '/' + path.split('/').filter(Boolean).slice(0, 1).join('/');

  // Sales paths always accessible
  if (SALES_PATHS.includes(basePath)) return true;

  const item = NAV_ITEMS.find(n => n.path === basePath);
  if (!item) return true;
  return itemSichtbar(item, role, userPermissions, false);
}
